import { defineStore } from 'pinia';

import type { ToastMessageOptions } from 'primevue/toast';
import { useToast } from 'primevue/usetoast';

type NotificationSeverity = 'success' | 'info' | 'warn' | 'error';

interface NotificationOptions {
  summary?: string;
  detail: string;
  life?: number;
  closable?: boolean;
  group?: string;
}

export const useNotificationsStore = defineStore('notifications', () => {
  const toast = useToast();

  const DEFAULT_LIFE = 3000;
  const ERROR_LIFE = 5000;
  const SUCCESS_LIFE = 3000;
  const INFO_LIFE = 4000;
  const WARNING_LIFE = 4000;

  function show(severity: NotificationSeverity, options: NotificationOptions): void {
    const toastOptions: ToastMessageOptions = {
      severity,
      summary: options.summary,
      detail: options.detail,
      life: options.life || DEFAULT_LIFE,
      closable: options.closable ?? true,
      group: options.group,
    };

    toast.add(toastOptions);
  }

  function success(detail: string, summary: string = 'Success'): void {
    show('success', { summary, detail, life: SUCCESS_LIFE });
  }

  function error(detail: string, summary: string = 'Error'): void {
    show('error', { summary, detail, life: ERROR_LIFE });
  }

  function info(detail: string, summary: string = 'Info'): void {
    show('info', { summary, detail, life: INFO_LIFE });
  }

  function warning(detail: string, summary: string = 'Warning'): void {
    show('warn', { summary, detail, life: WARNING_LIFE });
  }

  function operationFailed(operation: string, error?: unknown): void {
    const detail = error instanceof Error ? error.message : `Failed to ${operation}`;
    show('error', {
      summary: 'Operation Failed',
      detail,
      life: ERROR_LIFE,
    });
  }

  function operationSuccess(operation: string): void {
    show('success', {
      summary: 'Success',
      detail: `${operation} completed successfully`,
      life: SUCCESS_LIFE,
    });
  }

  function clear(group?: string): void {
    if (group) {
      toast.removeGroup(group);
    }
  }

  function clearAll(): void {
    toast.removeAllGroups();
  }

  return {
    show,
    success,
    error,
    info,
    warning,
    operationFailed,
    operationSuccess,
    clear,
    clearAll,
  };
});
